import React from 'react';
import { Target, Users, TrendingUp, Lightbulb } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';

const About: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const values = [
    {
      icon: Target,
      title: "Strategic Communications Planning",
      description: "Crafting comprehensive communication strategies that align with organizational goals and drive sustainable impact."
    },
    {
      icon: Users,
      title: "Stakeholder Engagement",
      description: "Building meaningful partnerships and fostering collaboration across diverse sectors and communities."
    },
    {
      icon: TrendingUp,
      title: "Digital Innovation",
      description: "Leveraging cutting-edge digital tools and platforms to amplify organizational reach and engagement."
    },
    {
      icon: Lightbulb,
      title: "Cross-Cultural Communication",
      description: "Bridging cultural divides through effective bilingual communication in English and Swahili."
    }
  ];

  return (
    <section id="about" className="py-20 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className={`text-4xl md:text-5xl font-light mb-6 ${
            isDark ? 'text-white' : 'text-charcoal'
          }`}>
            About <span className="gradient-text font-medium">Kennedy</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-sunrise-orange to-serengeti-gold mx-auto rounded-full"></div>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left side - Story */}
          <div className="animate-slide-in-left">
            <div className={`glass border rounded-3xl p-8 lg:p-12 shadow-2xl ${
              isDark ? 'glass-dark' : 'glass-light'
            }`}>
              <h3 className={`text-2xl font-medium mb-6 ${
                isDark ? 'text-white' : 'text-charcoal'
              }`}>
                Strategic Communications Expert
              </h3>
              
              <p className={`text-lg leading-relaxed mb-6 ${
                isDark ? 'text-gray-300' : 'text-gray-600'
              }`}>
                With over a decade of expertise in strategic communications, Kennedy Mmari has established himself as a 
                leading consultant in Tanzania's dynamic communications landscape. His unique approach combines 
                public relations excellence with digital innovation to drive meaningful impact.
              </p>
              
              <p className={`text-lg leading-relaxed mb-8 ${
                isDark ? 'text-gray-300' : 'text-gray-600'
              }`}>
                As CEO of Serengeti Bytes and founder of Tanzania Digital Awards, Kennedy has successfully led 
                over 250 communication campaigns, fostering partnerships between government, private sector, 
                and NGOs. His leadership philosophy centers on empowering organizations through strategic 
                storytelling and stakeholder engagement.
              </p>

              <div className="grid grid-cols-2 gap-6">
                <div className={`text-center p-4 rounded-2xl ${
                  isDark ? 'bg-white/5' : 'bg-gray-50/50'
                }`}>
                  <div className="text-3xl font-bold gradient-text mb-2">10+</div>
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Years Experience
                  </div>
                </div>
                <div className={`text-center p-4 rounded-2xl ${
                  isDark ? 'bg-white/5' : 'bg-gray-50/50'
                }`}>
                  <div className="text-3xl font-bold gradient-text mb-2">250+</div>
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Campaigns Led
                  </div>
                </div>
                <div className={`text-center p-4 rounded-2xl ${
                  isDark ? 'bg-white/5' : 'bg-gray-50/50'
                }`}>
                  <div className="text-3xl font-bold gradient-text mb-2">110+</div>
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Client Partnerships
                  </div>
                </div>
                <div className={`text-center p-4 rounded-2xl ${
                  isDark ? 'bg-white/5' : 'bg-gray-50/50'
                }`}>
                  <div className="text-3xl font-bold gradient-text mb-2">15K+</div>
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Youth Empowered
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Values */}
          <div className="animate-slide-in-right">
            <h3 className={`text-2xl font-medium mb-8 ${
              isDark ? 'text-white' : 'text-charcoal'
            }`}>
              Core Values & Philosophy
            </h3>
            
            <div className="space-y-6">
              {values.map((value, index) => (
                <div 
                  key={value.title}
                  className={`glass border rounded-2xl p-6 transition-all duration-300 hover:scale-105 ${
                    isDark ? 'glass-dark' : 'glass-light'
                  }`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-xl ${
                      isDark ? 'bg-sunrise-orange/20' : 'bg-sunrise-orange/10'
                    }`}>
                      <value.icon size={24} className="text-sunrise-orange" />
                    </div>
                    <div>
                      <h4 className={`text-xl font-medium mb-2 ${
                        isDark ? 'text-white' : 'text-charcoal'
                      }`}>
                        {value.title}
                      </h4>
                      <p className={`leading-relaxed ${
                        isDark ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        {value.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
